import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data/content";
import { useLang } from "../i18n/LanguageContext";
import { Logo } from "./Logo";
import LanguageToggle from "./LanguageToggle";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-2xl backdrop-saturate-150 bg-navy/65 border-b border-white/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between">
        <Logo />

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => {
            const active = location.pathname === l.path;
            return (
              <Link
                key={l.path}
                to={l.path}
                data-testid={`nav-link-${l.path.replace("/", "")}`}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-gold ${
                  active ? "text-gold" : "text-offwhite/80"
                }`}
              >
                {l.label[lang]}
              </Link>
            );
          })}
        </div>

        <div className="hidden lg:flex items-center gap-5">
          <LanguageToggle />
          <button
            data-testid="nav-cta-get-started"
            onClick={() => navigate("/contact")}
            className="bg-gold text-deepblack px-7 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-goldhover transition-colors duration-300"
          >
            {t.getStarted}
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <LanguageToggle />
          <button
            data-testid="nav-mobile-toggle"
            className="text-gold"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden mt-3 mx-4 border border-gold/20 bg-deepblack/95 backdrop-blur-xl px-6 py-6 flex flex-col gap-5"
        >
          {NAV_LINKS.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              className="text-offwhite/90 text-base font-medium hover:text-gold transition-colors"
            >
              {l.label[lang]}
            </Link>
          ))}
          <button
            onClick={() => navigate("/contact")}
            className="bg-gold text-deepblack px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] mt-2"
          >
            {t.getStarted}
          </button>
        </div>
      )}
    </header>
  );
}
